import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";

/**
 * Custom link handler: renders YouTube embeds + favicon preview links.
 */
const CustomParagraph = ({ children }) => {
  // Check if the only child is a link component
  if (
    React.Children.count(children) === 1 &&
    children[0]?.type === "a" &&
    children[0]?.props?.href
  ) {
    const link = children[0];
    const href = link.props.href;

    const youtubeRegex =
      /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/;
    const youtubeMatch = href.match(youtubeRegex);

    if (youtubeMatch?.[1]) {
      const videoId = youtubeMatch[1];
      return (
        <div className="yt-wrapper my-4">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}`}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="Embedded YouTube video"
          ></iframe>
        </div>
      );
    }

    try {
      const domain = new URL(href).hostname;
      const faviconUrl = `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;

      // Render the paragraph without top/bottom margin, and inject the icon
      return (
        <p className="my-0">
          <img
            src={faviconUrl}
            alt=""
            className="w-4 h-4 inline-block align-middle mr-1.5"
          />
          {link}
        </p>
      );
    } catch (e) {
      // Fallback for invalid URLs
      return <p>{children}</p>;
    }
  }

  // Render a normal paragraph for all other cases
  return <p>{children}</p>;
};

/**
 * Custom code block with collapse/copy buttons.
 * This component also intelligently detects if the code is a roadmap
 * and renders the RoadmapFlow component instead.
 */
const CodeBlock = ({ node, inline, className, children, ...props }) => {
  const [isCopied, setIsCopied] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const match = /language-(\w+)/.exec(className || "");
  const codeText = String(children).replace(/\n$/, "");
  const handleCopy = () => {
    navigator.clipboard.writeText(codeText);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const toggleCollapse = () => setIsCollapsed(!isCollapsed);

  const wrapperStyle = {
    borderRadius: "0.5rem",
    backgroundColor: "#1e293b",
    overflow: "hidden",
    margin: "1.5em 0",
  };

  const headerStyle = {
    display: "flex",
    alignItems: "center",
    padding: "0.5rem 1rem",
    gap: "0.75rem",
  };

  const contentWrapperStyle = {
    maxHeight: isCollapsed ? "0" : "1000px",
    opacity: isCollapsed ? "0" : "1",
    transition: "max-height 0.3s ease-out, opacity 0.2s ease-out",
    overflow: "auto",
    overflowX: "hidden",
  };

  const syntaxHighlighterStyle = {
    ...vscDarkPlus,
    'pre[class*="language-"]': {
      ...vscDarkPlus['pre[class*="language-"]'],
      backgroundColor: 'transparent', // The main wrapper handles the background
      padding: '0.5rem 1rem', // Apply consistent padding
      margin: 0, // Remove default margin
    },
    'code[class*="language-"]': {
      ...vscDarkPlus['code[class*="language-"]'],
      background: 'transparent', // Prevents the inner white stripe
    },
  };

  return !inline && match ? (
    <div style={wrapperStyle}>
      <div style={headerStyle}>
        <span className="text-xs text-gray-400 mr-auto capitalize">
          {match[1]}
        </span>
        <button
          onClick={toggleCollapse}
          className="text-xs text-gray-300 hover:text-white"
        >
          {isCollapsed ? "Expand" : "Collapse"}
        </button>
        <button
          onClick={handleCopy}
          className="text-xs text-gray-300 hover:text-white"
        >
          {isCopied ? "Copied!" : "Copy"}
        </button>
      </div>
      <div style={contentWrapperStyle}>
        <SyntaxHighlighter
          style={syntaxHighlighterStyle}
          language={match[1]}
          PreTag="div"
          wrapLines
          wrapLongLines
        >
          {codeText}
        </SyntaxHighlighter>
      </div>
    </div>
  ) : (
    <code className={className} {...props}>
      {children}
    </code>
  );
};

/**
 * Main Renderer
 */
export default function UniversalContentRenderer({ content }) {
  if (!content) return null;

  return (
    <div className="prose prose-sm max-w-none dark:prose-invert">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex]}
        components={{
          p: CustomParagraph,
          pre: ({ children }) => <>{children}</>,
          code: CodeBlock,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
